import { FC, useLayoutEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import Cookies from 'js-cookie'

import './Charts.scss';

type LineChartProps = {
    name: string,
    data: any[],
    data2?: any[],
    xLabels?: any,
    titleX?: string,
    titleY?: string,
    selectedQuarter?: { from: string, to: string }
};

const LineChart: FC<LineChartProps> = ({ name, data, data2, xLabels, titleX, titleY, selectedQuarter }) => {

    useLayoutEffect(() => {
        am5.ready(function () {
            am5.addLicense('AM5M326788873');
            am5.addLicense('AM5C326788873')
            const root = am5.Root.new(`${name}-chart`);
            root.setThemes([am5themes_Animated.new(root)]);

            let colorSet = am5.ColorSet.new(root, {});
            if (selectedQuarter && selectedQuarter.to) {
                if (data && data.length > 0) {
                    const indexofFrom = data.indexOf(data.find(e => e.name === selectedQuarter.from)) > -1 ? data.indexOf(data.find(e => e.name === selectedQuarter.from)) : 0
                    const indexofTo = data.indexOf(data.find(e => e.name === selectedQuarter.to)) > -1 ? data.indexOf(data.find(e => e.name === selectedQuarter.to)) : data.length - 1
                    data[indexofFrom].fillSettings = {
                        fill: colorSet.getIndex(3),
                    }
                    data[indexofTo].fillSettings = {
                        fill: colorSet.getIndex(0),
                    }
                }
            }

            let chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: false,
                    panY: false,
                    wheelX: "panX",
                })
            );

            let xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
            xRenderer.labels.template.setAll({
                oversizedBehavior: "wrap",
                maxWidth: 150,
                fontSize: Number(Cookies.get('globalFontSize'))*0.79, //11px,
                opposite: true,
                fill: (Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
                ...xLabels
                
            });

            let xAxis = chart.xAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: "name",
                    renderer: xRenderer,
                })
            );

            titleX && xAxis.children.push(
                am5.Label.new(root, {
                    text: titleX,
                    fill: (Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
                    fontWeight: "600",
                    fontStyle: "italic",
                    x: am5.p50,
                    centerX: am5.p50
                })
            );
            xAxis.data.setAll(data);

            let yRenderer = am5xy.AxisRendererY.new(root, { minGridDistance: 20 });
            yRenderer.labels.template.setAll({
                oversizedBehavior: "wrap",
                maxWidth: 150,
                fontSize: Number(Cookies.get('globalFontSize'))*0.79, //11px,
                fill: (Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x003b5c),

            });
            let yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
                maxDeviation: 0.1,
                renderer: yRenderer
            }));

            titleY && yAxis.children.unshift(
                am5.Label.new(root, {
                    rotation: -90,
                    text: titleY,
                    fill: (Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
                    fontWeight: "600",
                    fontStyle: "italic",
                    y: am5.p50,
                    centerX: am5.p50
                })
            );


            let series = chart.series.push(am5xy.LineSeries.new(root, {
                name: "Series 1",
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: "value",
                categoryXField: "name",
                tooltip: am5.Tooltip.new(root, {
                    pointerOrientation: "horizontal",
                    labelText: "[bold]{name}[/]\n{valueY}"
                })
            }));
            let series2 = chart.series.push(am5xy.SmoothedXLineSeries.new(root, {
                name: "Series 1",
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: "value",
                categoryXField: "name",
                tooltip: am5.Tooltip.new(root, {
                    pointerOrientation: "horizontal",
                    labelText: "[bold]{name}[/]\n{valueY}"
                })
            }));

            series.fills.template.setAll({
                visible: true,
                fillOpacity: 0.5,
                templateField: "fillSettings"
            });

            // Add cursor
            chart.set("cursor", am5xy.XYCursor.new(root, {
                behavior: "zoomXY"
            }));

            series.data.setAll(data);
            if (data2) series2.data.setAll(data2);


            // Make stuff animate on load
            // https://www.amcharts.com/docs/v5/concepts/animations/
            series.appear(1000);
            chart.appear(1000, 100);
        });
    }, []);// eslint-disable-line

    return <div id={`${name}-chart`} className="chart-container" />;
};

export default LineChart;
